import { Booking, BookingStatus, BookingPaymentStatus } from '@/lib/bookings/types';

export interface TimelineEvent {
  id: string;
  title: string;
  description?: string;
  date: Date;
  type: 'success' | 'warning' | 'danger' | 'neutral' | 'info';
  icon?: string;
}

interface BookingTimelineProps {
  booking: Booking;
}

function formatDateTime(date: Date | string | undefined): string {
  if (!date) return '-';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getEventIcon(type: TimelineEvent['type']): string {
  switch (type) {
    case 'success':
      return '✓';
    case 'warning':
      return '⏳';
    case 'danger':
      return '✕';
    case 'info':
      return '💳';
    default:
      return '●';
  }
}

function getEventColor(type: TimelineEvent['type']) {
  switch (type) {
    case 'success':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800';
    case 'warning':
      return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800';
    case 'danger':
      return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800';
    case 'info':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800';
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400 border-gray-200 dark:border-gray-700';
  }
}

function getDotColor(type: TimelineEvent['type']) {
  switch (type) {
    case 'success':
      return 'bg-green-500';
    case 'warning':
      return 'bg-yellow-500';
    case 'danger':
      return 'bg-red-500';
    case 'info':
      return 'bg-blue-500';
    default:
      return 'bg-gray-400';
  }
}

export function BookingTimeline({ booking }: BookingTimelineProps) {
  const events: TimelineEvent[] = [];

  // Booking Created
  events.push({
    id: 'created',
    title: 'Booking Created',
    description: `Booking #${booking.bookingNumber} was created`,
    date: new Date(booking.createdAt),
    type: 'neutral',
  });

  // Confirmed
  if (booking.confirmedAt) {
    events.push({
      id: 'confirmed',
      title: 'Booking Confirmed',
      description: 'The booking has been confirmed',
      date: new Date(booking.confirmedAt),
      type: 'success',
    });
  }

  // Cancelled
  if (booking.cancelledAt) {
    events.push({
      id: 'cancelled',
      title: 'Booking Cancelled',
      description: 'The booking has been cancelled',
      date: new Date(booking.cancelledAt),
      type: 'danger',
    });
  }

  // Completed
  if (booking.completedAt) {
    events.push({
      id: 'completed',
      title: 'Booking Completed',
      description: 'The booking has been completed successfully',
      date: new Date(booking.completedAt),
      type: 'success',
    });
  }

  // Refunded (payment status)
  if (booking.paymentStatus === BookingPaymentStatus.REFUNDED) {
    // Use updatedAt as approximate refunded time since no specific field exists
    events.push({
      id: 'refunded',
      title: 'Payment Refunded',
      description: 'The payment has been refunded',
      date: new Date(booking.updatedAt),
      type: 'info',
    });
  }

  // Sort events by date ascending (oldest first)
  events.sort((a, b) => a.date.getTime() - b.date.getTime());

  if (events.length === 0) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400">
        No timeline events available.
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Vertical line */}
      <div className="absolute left-4 top-0 h-full w-0.5 bg-gray-200 dark:bg-dark-3" />

      <div className="space-y-4">
        {events.map((event, index) => (
          <div key={event.id} className="relative flex items-start gap-4 pl-10">
            {/* Dot */}
            <div
              className={`absolute left-2.5 top-1.5 z-10 flex h-3 w-3 items-center justify-center rounded-full ${getDotColor(
                event.type
              )}`}
            >
              <span className="text-[8px] text-white">{getEventIcon(event.type)}</span>
            </div>

            {/* Event card */}
            <div
              className={`w-full rounded-lg border p-3 ${getEventColor(event.type)}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-medium text-sm">{event.title}</p>
                  {event.description && (
                    <p className="mt-0.5 text-xs opacity-80">{event.description}</p>
                  )}
                </div>
                <span className="shrink-0 text-xs opacity-70">
                  {formatDateTime(event.date)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
